import Footer from "./Footer";
import Navbar from "./Navbar";
import LoanCalculator from "./LoanCalculator";
import WhyChooseUs from "./WhyChooseUs";
import HowItWorks from "./HowItWorks";
import Seo from "./Seo";
import { FaStar, FaRegStar } from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import heroFamily from "../assets/v2/hero-family.webp";
import iconCard from "../assets/v2/feature-icon-card.webp";
import iconNoHiddenCharges from "../assets/v2/feature-icon-no-hidden-charges.webp";
import iconPaperless from "../assets/v2/feature-icon-paperless.webp";

const SITE_URL = "https://eazypaisa.com";
const COMPANY_NAME = import.meta.env.VITE_COMPANY_NAME || "EazyPaisa";

const homeSchema = [
  {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: COMPANY_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/eazypaisa-og.png`,
    description:
      "Instant personal loans for salaried individuals with fast approval, minimal documentation, and secure disbursal.",
    telephone: import.meta.env.VITE_COMPANY_PHONE || undefined,
    email: import.meta.env.VITE_COMPANY_EMAIL || undefined,
    address: import.meta.env.VITE_COMPANY_ADDRESS
      ? {
          "@type": "PostalAddress",
          streetAddress: import.meta.env.VITE_COMPANY_ADDRESS,
        }
      : undefined,
    alternateName: ["Easy Paisa", "EazyPaisa Loan", "Easypaisa"],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: COMPANY_NAME,
    url: SITE_URL,
  },
];

const TRUST_BADGES = [
  { icon: "🔒", label: "SECURE & ENCRYPTED" },
  { icon: "⏱", label: "INSTANT APPROVAL" },
  { icon: "👁", label: "TRANSPARENT" },
];

const FEATURES = [
  {
    icon: iconCard,
    title: "Smarter than a card",
    description: "Track, budget, and earn rewards while spending securely with one powerful card.",
  },
  {
    icon: iconNoHiddenCharges,
    title: "No hidden surprises",
    description: "Clear rates, no fees in disguise — full transparency at every step.",
  },
  {
    icon: iconPaperless,
    title: "Skip the paperwork",
    description: "Digital onboarding in minutes. Safe. Fast. 100% paperless.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "EazyPaisa made getting a loan so simple! I got approved in minutes and the money was in my account the same day. Highly recommend!",
    name: "Rajesh Sharma",
    initials: "RS",
    role: "Small Business Owner",
    rating: 5,
  },
  {
    quote:
      "No paperwork, no hassle. Just a quick application and instant approval. This is the future of lending!",
    name: "Priya Kapoor",
    initials: "PK",
    role: "Freelancer",
    rating: 5,
  },
  {
    quote:
      "Transparent rates, flexible terms, and amazing customer support. EazyPaisa has been a lifesaver for my business!",
    name: "Amit Mehta",
    initials: "AM",
    role: "Entrepreneur",
    rating: 5,
  },
  {
    quote:
      "EazyPaisa made getting a loan so simple! I got approved in minutes and the money was in my account the same day. Highly recommend!",
    name: "Rajesh Sharma",
    initials: "RS",
    role: "Small Business Owner",
    rating: 4,
  },
  {
    quote:
      "No paperwork, no hassle. Just a quick application and instant approval. This is the future of lending!",
    name: "Priya Kapoor",
    initials: "PK",
    role: "Freelancer",
    rating: 5,
  },
];

function EazyPaisaLanding() {
  return (
    <div className="min-h-screen bg-ep-ivory">
      <Seo
        title="Instant Loan Online | ₹1 Lakh Personal Loan"
        description="Get an instant loan online with EazyPaisa. Apply for a ₹1 lakh personal loan, short-term loan or quick cash loan without collateral. Check eligibility today."
        path="/"
        schema={homeSchema}
      />
      <Navbar />

      <main>
        {/* Hero + feature cards share the cream background */}
        <section id="home" className="bg-ep-cream pt-[72px] xl:pt-[88px]">
          <div className="page-container grid items-center gap-10 pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,615px)] lg:gap-12 xl:pt-[46px]">
            {/* Copy */}
            <div className="flex flex-col items-start">
              <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-ep-line bg-ep-pill px-4 py-1.5 text-[11px] tracking-[0.02em] text-ep-green sm:gap-x-5 sm:text-[12px]">
                {TRUST_BADGES.map((badge, i) => (
                  <li key={badge.label} className="flex items-center gap-x-3 sm:gap-x-5">
                    {i > 0 && <span aria-hidden className="hidden text-ep-muted sm:inline">·</span>}
                    <span>
                      <span aria-hidden className="mr-1 text-[10px]">{badge.icon}</span>
                      {badge.label}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-7 font-heading text-[22px] italic text-ep-green sm:text-[26px]">
                Sapno ka eazy rasta
              </p>

              <h1 className="mt-5 font-serif text-[clamp(44px,1.5rem+4.2vw,76px)] font-normal uppercase leading-[1.05] text-ep-green">
                Your trusted
                <br />
                loan partner
              </h1>

              <p className="mt-8 max-w-[520px] text-base leading-relaxed text-ep-muted sm:text-lg">
                Instant loans with fast approval and secure cash transfer.
              </p>

              <a
                href={import.meta.env.VITE_WEBSITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="ep-btn ep-btn-dark mt-7"
              >
                Apply Now
                <FiArrowUpRight aria-hidden className="text-base" />
              </a>
            </div>

            {/* Family photo — square corners except a large bottom-right curve */}
            <div className="overflow-hidden rounded-br-[64px] lg:rounded-br-[90px]">
              <img
                src={heroFamily}
                alt="A smiling family with their luggage, setting off on a trip"
                className="aspect-[615/580] w-full object-cover object-[72%_center]"
                width={1165}
                height={704}
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>

          {/* Feature cards */}
          <div className="page-container pb-16 pt-12 xl:pt-[50px]">
            <ul className="grid gap-6 md:grid-cols-3 md:gap-8">
              {FEATURES.map((feature) => (
                <li
                  key={feature.title}
                  className="flex flex-col items-center rounded-xl border border-ep-border bg-white px-8 pb-7 pt-6 text-center"
                >
                  <img src={feature.icon} alt="" aria-hidden width={32} height={32} className="h-8 w-8" loading="lazy" />
                  <h2 className="mt-10 font-serif text-[22px] font-normal uppercase tracking-[0.01em] text-ep-green lg:text-[26px]">
                    {feature.title}
                  </h2>
                  <p className="mt-3 max-w-[340px] text-[15px] leading-[1.65] text-ep-muted">
                    {feature.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Loan Calculator */}
        <LoanCalculator />

        {/* Why Choose Us */}
        <WhyChooseUs />

        {/* How It Works */}
        <HowItWorks />

        {/* Customer Testimonials */}
        <section className="bg-ep-ivory py-20 lg:pb-[50px] lg:pt-[100px]" aria-labelledby="testimonials-title">
          <div className="page-container">
            <h2 id="testimonials-title" className="ep-section-title text-ep-green">
              What our customers say
            </h2>
            <p className="mt-6 text-base text-ep-muted lg:text-[17px]">
              Real stories from real people who trust EazyPaisa
            </p>

            <ul className="ep-no-scrollbar -mx-5 mt-12 flex snap-x snap-mandatory scroll-px-5 gap-6 overflow-x-auto px-5 pb-2 md:-mx-10 md:scroll-px-10 md:px-10 lg:mt-[68px] xl:mx-0 xl:grid xl:grid-cols-5 xl:overflow-visible xl:px-0">
              {TESTIMONIALS.map((t, i) => (
                <li
                  key={i}
                  className="flex min-h-[343px] w-[260px] shrink-0 snap-start flex-col rounded-[20px] border border-ep-border bg-white p-7 xl:w-auto"
                >
                  <div className="flex gap-2.5 text-ep-gold" role="img" aria-label={`${t.rating} out of 5 stars`}>
                    {Array.from({ length: 5 }, (_, s) =>
                      s < t.rating ? <FaStar key={s} className="h-4 w-4" /> : <FaRegStar key={s} className="h-4 w-4" />
                    )}
                  </div>
                  <blockquote className="mt-6 flex-1 text-[15px] leading-[26px] text-ep-quote">
                    "{t.quote}"
                  </blockquote>
                  <div className="mt-6 flex items-center gap-3">
                    <span aria-hidden className="w-7 shrink-0 text-center font-serif text-lg font-medium text-ep-green">
                      {t.initials}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold text-ep-green">{t.name}</p>
                      <p className="mt-0.5 text-[13px] text-ep-quote">{t.role}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default EazyPaisaLanding;
